import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { motion, useReducedMotion } from 'framer-motion';
import GooglePlacesAutocomplete from '../components/GooglePlacesAutocomplete';
import SchoolAutocomplete from '../components/SchoolAutocomplete';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  ArrowRight,
  MapPin,
  Sparkles,
  Shield,
  CheckCircle2,
  Building2,
  Key,
} from 'lucide-react';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_5935cf64-512e-4adb-972b-b8d08d999032/artifacts/9m3z1jc8_thumbnail_PHOTO-2025-02-05-22-08-51.jpg';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Brand colors (kept centralized for clarity)
const HONEY = '#F0AC25';
const HONEY_DARK = '#D4941A';
const NAVY = '#0A1F38';
const NAVY_DEEP = '#061427';
const CREAM = '#FAF7F2';

// Ontario biasing — non-strict so we can catch non-Ontario picks and offer expansion alerts.
const ONTARIO_BOUNDS = {
  sw: { lat: 41.6765, lng: -95.1562 },
  ne: { lat: 56.8591, lng: -74.3440 },
};

const isOntario = (place) =>
  !!place && (place.state || '').trim().toLowerCase() === 'ontario';

// ---- Hero floating photo cluster config ----
// Real Unsplash URLs (direct — no API key required for unsplash.com/photos).
// Tight ±3° rotations, consistent z-index with the bedroom/listing card on top.
const HERO_CARDS = [
  {
    key: 'toronto-skyline',
    src: 'https://images.unsplash.com/photo-1517090504586-fde19ea6066f?w=900&q=85&auto=format&fit=crop',
    alt: 'Toronto skyline with CN Tower',
    label: 'Greater Toronto Area',
    sub: 'Launching Fall 2026',
    w: 'w-56',
    h: 'h-40',
    top: '-2%',
    left: '46%',
    rotate: 3,
    z: 20,
    delay: 0.4,
    floatRange: [-8, 10],
    floatDur: 11,
  },
  {
    key: 'study-desk',
    src: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=900&q=85&auto=format&fit=crop',
    alt: 'Student studying at a desk',
    label: '12-month lease',
    sub: 'Real terms, upfront',
    w: 'w-52',
    h: 'h-60',
    top: '34%',
    left: '56%',
    rotate: -2,
    z: 30,
    delay: 0.55,
    floatRange: [-10, 8],
    floatDur: 10,
  },
  {
    key: 'keys-moving',
    src: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=900&q=85&auto=format&fit=crop',
    alt: 'Keys handed over on move-in day',
    label: 'Move-in ready',
    sub: 'Verified landlord',
    w: 'w-44',
    h: 'h-36',
    top: '58%',
    left: '4%',
    rotate: 2,
    z: 25,
    delay: 0.7,
    floatRange: [-6, 12],
    floatDur: 8.5,
  },
  {
    key: 'warm-lamp',
    src: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=900&q=85&auto=format&fit=crop',
    alt: 'Warm lamp-lit living room',
    label: 'All-in $1,010 / mo',
    sub: 'Rent + utilities + wifi',
    w: 'w-48',
    h: 'h-56',
    top: '62%',
    left: '40%',
    rotate: -2,
    z: 40,
    delay: 0.85,
    floatRange: [-11, 9],
    floatDur: 12,
  },
  {
    key: 'cozy-room',
    src: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=900&q=85&auto=format&fit=crop',
    alt: 'Cozy student bedroom with warm lighting',
    label: 'Cozy private room',
    sub: 'Brampton · near Sheridan',
    w: 'w-64',
    h: 'h-80',
    top: '4%',
    left: '2%',
    rotate: -3,
    z: 50,
    delay: 0.25,
    floatRange: [-12, 12],
    floatDur: 9,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

// ------------------------------------------------------------------
// CountUp — counts from 0 to `to` when the element scrolls into view.
// ------------------------------------------------------------------
const CountUp = ({ to, duration = 1.4 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (reduce || to === 0) {
      setVal(to);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const startTime = performance.now();
            const tick = (now) => {
              const p = Math.min(1, (now - startTime) / (duration * 1000));
              const eased = 1 - Math.pow(1 - p, 3);
              setVal(Math.round(to * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration, reduce]);

  return <span ref={ref}>{val}</span>;
};

// ------------------------------------------------------------------
// Custom home / rental themed SVG icons for trust pillars.
// Drawn inline, stroke-based, 40x40 viewBox. Honey color by default.
// ------------------------------------------------------------------
const HouseCheckIcon = ({ className = 'h-[34px] w-[34px]', color = '#F0AC25' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none" className={className} aria-hidden>
    <path
      d="M7 18.2 L20 7 L33 18.2 L33 32 Q33 33.5 31.5 33.5 L8.5 33.5 Q7 33.5 7 32 Z"
      stroke={color}
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <path
      d="M16.5 33.5 V23 H23.5 V33.5"
      stroke={color}
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    {/* Check badge */}
    <circle cx="30" cy="11.5" r="5.4" fill={color} />
    <path
      d="M27.6 11.8 L29.3 13.4 L32.4 10.2"
      stroke="#FFFFFF"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const ReceiptIcon = ({ className = 'h-[34px] w-[34px]', color = '#F0AC25' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none" className={className} aria-hidden>
    <path
      d="M11 6 L29 6 L29 34 L26 31.5 L23 34 L20 31.5 L17 34 L14 31.5 L11 34 Z"
      stroke={color}
      strokeWidth="2.2"
      strokeLinejoin="round"
      fill="none"
    />
    <line x1="15" y1="13.5" x2="25" y2="13.5" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <line x1="15" y1="19" x2="22" y2="19" stroke={color} strokeWidth="2" strokeLinecap="round" />
    {/* Honey highlight */}
    <circle cx="25" cy="24.5" r="2.3" fill={color} />
  </svg>
);

const KeyRingIcon = ({ className = 'h-[34px] w-[34px]', color = '#F0AC25' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none" className={className} aria-hidden>
    {/* Key ring */}
    <circle cx="13.5" cy="20" r="6.8" stroke={color} strokeWidth="2.2" fill="none" />
    <circle cx="13.5" cy="20" r="2" fill={color} />
    {/* Shaft */}
    <line x1="20.3" y1="20" x2="33" y2="20" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    {/* Teeth */}
    <line x1="29.5" y1="20" x2="29.5" y2="25.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    <line x1="33" y1="20" x2="33" y2="24" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);


// ------------------------------------------------------------------
// HeroCard — a floating photo tile with gentle continuous drift.
// ------------------------------------------------------------------
const HeroCard = ({ card }) => {
  const reduce = useReducedMotion();
  const [a, b] = card.floatRange;

  const float = reduce
    ? {}
    : {
        y: [a, b, a],
        rotate: [card.rotate, card.rotate + 0.8, card.rotate],
      };

  return (
    <motion.div
      initial={{ opacity: 0, y: 36, scale: 0.92, rotate: card.rotate }}
      animate={{ opacity: 1, y: 0, scale: 1, rotate: card.rotate }}
      transition={{
        duration: 0.85,
        delay: card.delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      style={{
        top: card.top,
        left: card.left,
        zIndex: card.z,
      }}
      className="absolute"
      data-testid={`hero-card-${card.key}`}
    >
      <motion.div
        animate={float}
        transition={{
          duration: card.floatDur,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: card.delay + 1.2,
        }}
        className={`${card.w} ${card.h} relative rounded-[22px] overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/10`}
      >
        <img
          src={card.src}
          alt={card.alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,31,56,0) 40%, rgba(10,31,56,0.55) 100%)',
          }}
        />
        <div className="absolute bottom-2.5 left-2.5 right-2.5">
          <div
            className="rounded-xl px-3 py-2 border"
            style={{
              background: 'rgba(10,31,56,0.88)',
              borderColor: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 8px 20px -8px rgba(0,0,0,0.5)',
            }}
          >
            <p
              className="text-[10px] uppercase tracking-[0.12em] font-bold mb-0.5"
              style={{ color: HONEY }}
            >
              {card.label}
            </p>
            <p className="text-white text-[12.5px] font-semibold leading-tight">
              {card.sub}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ------------------------------------------------------------------
// Landing page
// ------------------------------------------------------------------
const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const reduce = useReducedMotion();

  // Nav is always solid-cream because the top of the hero uses a cream→navy
  // gradient — this keeps logo/links fully legible throughout.
  const scrolled = true;

  // Hero search state
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  // Expansion-request dialog state
  const [showExpansion, setShowExpansion] = useState(false);
  const [expansionContext, setExpansionContext] = useState(null);
  const [expansionEmail, setExpansionEmail] = useState('');
  const [expansionSchool, setExpansionSchool] = useState(null);
  const [expansionStatus, setExpansionStatus] = useState('idle');
  const [expansionError, setExpansionError] = useState('');

  const goSignup = () => navigate('/signup');
  const goLandlordSignup = () => navigate('/signup?role=landlord');

  const handleSearch = (e) => {
    e.preventDefault();
    if (!selectedPlace) {
      goSignup();
      return;
    }
    if (isOntario(selectedPlace)) {
      const q =
        selectedPlace.types?.some((t) =>
          ['university', 'school', 'establishment'].includes(t)
        )
          ? `school=${encodeURIComponent(selectedPlace.name || '')}`
          : `city=${encodeURIComponent(selectedPlace.city || selectedPlace.name || '')}`;
      navigate(`/search?${q}`);
    } else {
      openExpansion(selectedPlace);
    }
  };

  const openExpansion = (place) => {
    setExpansionContext(place);
    setExpansionEmail('');
    setExpansionSchool(null);
    setExpansionStatus('idle');
    setExpansionError('');
    setShowExpansion(true);
  };

  const handleDialogChange = (open) => {
    setShowExpansion(open);
    if (!open) {
      setExpansionEmail('');
      setExpansionSchool(null);
      setExpansionStatus('idle');
      setExpansionError('');
    }
  };

  const submitExpansion = async (e) => {
    e.preventDefault();
    if (!expansionEmail || !/.+@.+\..+/.test(expansionEmail)) {
      setExpansionError("Hmm, that email doesn't look right.");
      return;
    }
    setExpansionStatus('submitting');
    setExpansionError('');
    try {
      const res = await fetch(`${API_BASE}/api/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: expansionEmail,
          requested_city: expansionContext?.city || expansionContext?.name || null,
          requested_state: expansionContext?.state || null,
          requested_country: expansionContext?.country || null,
          requested_school: expansionSchool?.name || null,
          requested_school_place_id: expansionSchool?.place_id || null,
          requested_school_lat: expansionSchool?.lat || null,
          requested_school_lng: expansionSchool?.lng || null,
          intent: 'expansion_request',
          source: 'hero_search',
        }),
      });
      if (!res.ok) throw new Error('alert_failed');
      setExpansionStatus('success');
    } catch {
      setExpansionStatus('error');
      setExpansionError('Something went wrong saving your request. Try again?');
    }
  };

  const cityLabel =
    expansionContext?.city || expansionContext?.name || 'your area';

  const expansionCountries = expansionContext?.country_code
    ? [expansionContext.country_code.toLowerCase()]
    : ['ca'];

  // Three trust pillars.
  const pillars = [
    {
      icon: HouseCheckIcon,
      title: 'Verified ownership',
      body:
        'Every landlord proves they own or manage the property before their first listing goes live. No paperwork, no listing.',
    },
    {
      icon: ReceiptIcon,
      title: 'All-in pricing',
      body:
        'Rent, utilities, internet, and any fees — visible upfront. The number you see is the number you pay.',
    },
    {
      icon: KeyRingIcon,
      title: 'Real lease terms',
      body:
        'Every listing shows the lease length, the deposit, and what happens if your plans change. No surprises on signing day.',
    },
  ];

  // Nav links on dark hero need honey hover; on scrolled cream they need navy hover.
  const navTextColor = scrolled ? 'text-[#0A1F38]' : 'text-[#FAF7F2]';

  return (
    <div
      className="min-h-screen"
      style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      {/* ------------------------------ Navigation ------------------------------ */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#FAF7F2]/92 backdrop-blur-xl border-b border-[#0A1F38]/8 shadow-[0_1px_0_0_rgba(10,31,56,0.04)]'
            : 'bg-transparent'
        }`}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-3.5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 group"
            data-testid="nav-home"
          >
            <img
              src={LOGO_URL}
              alt="Sturent"
              className="h-10 w-10 rounded-xl object-cover transition-transform group-hover:scale-[1.03]"
            />
            <span
              className={`font-extrabold text-lg tracking-tight transition-colors ${navTextColor}`}
              style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
            >
              Sturent
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <Button
                onClick={() =>
                  navigate(user?.user_type === 'landlord' ? '/signup' : '/search')
                }
                className="h-11 px-5 rounded-full font-semibold text-[#0A1F38] transition-transform hover:-translate-y-[1px]"
                style={{
                  background: HONEY,
                }}
                data-testid="nav-continue"
              >
                Continue
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={goSignup}
                  className="hidden sm:inline-flex items-center h-11 px-4 rounded-full text-[14px] font-medium text-[#0A1F38] hover:bg-[#0A1F38]/5 transition-colors"
                  data-testid="nav-signin"
                >
                  Sign in
                </button>
                <Button
                  onClick={goSignup}
                  className="h-11 px-5 rounded-full text-[14px] font-medium text-[#0A1F38] transition-transform hover:-translate-y-[1px]"
                  style={{
                    background: HONEY,
                  }}
                  data-testid="nav-get-started"
                >
                  Get started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* --------------------------------- Hero --------------------------------- */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(180deg, ${NAVY_DEEP} 0%, ${NAVY} 55%, #0D2644 100%)`,
          color: CREAM,
        }}
      >
        {/* Ambient emerald glow bottom-right */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            bottom: '-20%',
            right: '-8%',
            width: 600,
            height: 600,
            background:
              'radial-gradient(50% 50% at 50% 50%, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0) 70%)',
            filter: 'blur(50px)',
          }}
        />
        {/* Grain */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.035] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-32 pb-16 lg:pt-40 lg:pb-20">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
            {/* Left column */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="relative z-10 max-w-xl"
            >
              {/* Launch badge */}
              <motion.div variants={fadeUp}>
                <span
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-6 border"
                  style={{
                    background: 'rgba(240,172,37,0.1)',
                    borderColor: `${HONEY}55`,
                    color: HONEY,
                  }}
                  data-testid="launch-badge"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Launching in the Greater Toronto Area — Fall 2026
                </span>
              </motion.div>

              {/* Headline — Inter, weight 900, 44px mobile / 72px desktop, -0.035em, lh 1.0 */}
              <motion.h1
                variants={fadeUp}
                className="mb-5 text-[44px] md:text-[56px] lg:text-[72px]"
                style={{
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                  fontWeight: 900,
                  letterSpacing: '-0.035em',
                  lineHeight: 1.0,
                  color: CREAM,
                }}
                data-testid="hero-headline"
              >
                Rent with <span style={{ color: HONEY }}>confidence</span>
                <span style={{ color: HONEY }}>.</span>
              </motion.h1>

              {/* Subhead */}
              <motion.p
                variants={fadeUp}
                className="leading-relaxed mb-8 max-w-md text-[17px] md:text-[19px]"
                style={{ color: 'rgba(250,247,242,0.82)' }}
                data-testid="hero-subhead"
              >
                Verified landlords. All-in pricing. Real lease terms. Built for
                students moving to the Greater Toronto Area.
              </motion.p>

              {/* Search pill */}
              <motion.form
                variants={fadeUp}
                onSubmit={handleSearch}
                className="mb-6"
              >
                <div
                  className="p-2 flex flex-col sm:flex-row gap-2 rounded-2xl"
                  style={{
                    background: 'rgba(255,253,250,0.96)',
                    border: '1px solid rgba(255,253,250,0.4)',
                    boxShadow:
                      '0 20px 60px -20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.8)',
                  }}
                >
                  <div className="flex-1">
                    <GooglePlacesAutocomplete
                      type="mixed"
                      placeholder="Search by school, city, or neighborhood"
                      value={searchInput}
                      onChange={(v) => {
                        setSearchInput(v);
                        if (selectedPlace && v !== (selectedPlace.name || '')) {
                          setSelectedPlace(null);
                        }
                      }}
                      onSelect={(place) => {
                        setSelectedPlace(place);
                        setSearchInput(place.name || '');
                        if (!isOntario(place)) openExpansion(place);
                      }}
                      countries={['ca']}
                      bounds={ONTARIO_BOUNDS}
                      className="[&_input]:h-14 [&_input]:border-0 [&_input]:bg-transparent [&_input]:rounded-xl [&_input]:text-base [&_input]:text-[#0A1F38] [&_input]:focus:ring-2 [&_input]:focus:ring-[#F0AC25]/40"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="h-14 px-7 rounded-xl font-semibold text-[#0A1F38] transition-transform hover:-translate-y-[1px]"
                    style={{
                      background: HONEY,
                    }}
                    data-testid="hero-search-btn"
                  >
                    <Search className="mr-2 h-5 w-5" />
                    Search
                  </Button>
                </div>
              </motion.form>

              {/* CTA row */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Button
                  onClick={goSignup}
                  className="h-13 px-6 py-5 rounded-full font-semibold text-[#0A1F38] transition-transform hover:-translate-y-[1px]"
                  style={{
                    background: HONEY,
                  }}
                  data-testid="hero-find-housing"
                >
                  Find Housing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={goLandlordSignup}
                  variant="outline"
                  className="h-13 px-6 py-5 rounded-full font-semibold bg-transparent hover:bg-white/10 transition-colors"
                  style={{
                    color: CREAM,
                    borderColor: 'rgba(250,247,242,0.25)',
                  }}
                  data-testid="hero-list-property"
                >
                  <Building2 className="mr-2 h-5 w-5" />
                  List Your Property
                </Button>
              </motion.div>

              {/* Trust strip */}
              <motion.div
                variants={fadeUp}
                className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-2.5 text-[14px]"
                style={{ color: 'rgba(250,247,242,0.7)' }}
              >
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" style={{ color: HONEY }} />
                  Verified landlords only
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" style={{ color: HONEY }} />
                  No hidden fees
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" style={{ color: HONEY }} />
                  Ontario Standard Lease
                </span>
              </motion.div>
            </motion.div>

            {/* Right column — floating card cluster */}
            <div className="relative hidden lg:block">
              <div className="relative h-[560px] w-full">
                {HERO_CARDS.map((card) => (
                  <HeroCard key={card.key} card={card} />
                ))}

                {/* Floating trust badge — top right */}
                <motion.div
                  initial={{ opacity: 0, y: -20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute"
                  style={{ top: '18%', right: '-4%', zIndex: 50 }}
                >
                  <motion.div
                    animate={reduce ? {} : { y: [-6, 6, -6] }}
                    transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                    className="px-4 py-3 rounded-2xl flex items-center gap-3"
                    style={{
                      background: 'rgba(250,247,242,0.97)',
                      boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
                    }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${HONEY}22` }}
                    >
                      <Shield className="h-5 w-5" style={{ color: HONEY_DARK }} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#0A1F38]/60">
                        Ownership checked
                      </p>
                      <p
                        className="text-sm font-bold text-[#0A1F38]"
                        style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                      >
                        Verified landlord
                      </p>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Floating all-in badge — bottom right */}
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute"
                  style={{ bottom: '6%', right: '-6%', zIndex: 50 }}
                >
                  <motion.div
                    animate={reduce ? {} : { y: [6, -6, 6] }}
                    transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                    className="px-4 py-3 rounded-2xl flex items-center gap-3"
                    style={{
                      background: NAVY_DEEP,
                      border: `1px solid ${HONEY}55`,
                      boxShadow: '0 30px 60px -20px rgba(0,0,0,0.6)',
                    }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${HONEY}22` }}
                    >
                      <Key className="h-5 w-5" style={{ color: HONEY }} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: `${HONEY}cc` }}>
                        Move-in ready
                      </p>
                      <p
                        className="text-sm font-bold"
                        style={{ color: CREAM, fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                      >
                        Keys, not surprises
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Trust pillars: three things every listing earns ------ */}
      <section
        className="relative pt-16 pb-24 lg:pt-20 lg:pb-32"
        style={{ background: CREAM }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
            className="max-w-2xl mb-14"
          >
            <motion.p
              variants={fadeUp}
              className="text-xs font-bold uppercase tracking-[0.18em] mb-3"
              style={{ color: HONEY_DARK }}
              data-testid="trust-eyebrow"
            >
              How Sturent works
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-extrabold leading-[1.05] tracking-tight text-[#0A1F38]"
              style={{
                fontFamily: "'Inter Tight', 'Inter', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3.25rem)',
              }}
            >
              Three things every listing has to earn.
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-[#0A1F38]/70 max-w-2xl leading-relaxed text-[16px] md:text-[18px] text-balance"
            >
              Before a place shows up on Sturent, it clears the same three
              checks — every time, no exceptions.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-5 lg:gap-6"
          >
            {pillars.map((p, idx) => (
              <motion.div
                key={p.title}
                variants={fadeUp}
                whileHover={reduce ? {} : { y: -4 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[22px] p-7 lg:p-8 bg-white border border-[#0A1F38]/06 shadow-[0_4px_20px_-4px_rgba(10,31,56,0.06)]"
                style={{ borderColor: 'rgba(10,31,56,0.06)' }}
                data-testid={`protect-card-${p.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-+$/, '')}`}
              >
                {/* Index mark */}
                <span
                  className="absolute top-6 right-6 text-[11px] font-bold uppercase tracking-wider"
                  style={{ color: `${HONEY_DARK}` }}
                >
                  0{idx + 1}
                </span>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                  style={{
                    background: `${HONEY}18`,
                    border: `1px solid ${HONEY}33`,
                  }}
                >
                  <p.icon className="h-9 w-9" color={HONEY} />
                </div>
                <h3
                  className="font-bold text-xl mb-2 text-[#0A1F38]"
                  style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                >
                  {p.title}
                </h3>
                <p className="text-[#0A1F38]/75 leading-relaxed text-[16px]">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ------------------------ Built from a real story ---------------------- */}
      <section
        className="relative py-20 lg:py-24 overflow-hidden"
        style={{ background: '#F3EFE7' }}
      >
        {/* Decorative doorway arch behind heading — very faint honey */}
        <div
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
          style={{ top: '10%', width: 520, height: 520, opacity: 0.05 }}
        >
          <svg viewBox="0 0 520 520" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <path
              d="M 60 520 V 260 A 200 200 0 0 1 460 260 V 520"
              stroke={HONEY}
              strokeWidth="18"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              className="text-xs font-bold uppercase tracking-[0.18em] mb-4"
              style={{ color: HONEY_DARK }}
              data-testid="origin-eyebrow"
            >
              Our origin
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="leading-[1.05] text-[32px] md:text-[44px] lg:text-[52px]"
              style={{
                fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: NAVY,
              }}
            >
              Built from a real story.
            </motion.h2>
            {/* Honey accent line */}
            <motion.div
              variants={fadeUp}
              aria-hidden
              className="mx-auto mt-4 mb-6 rounded-full"
              style={{ width: 72, height: 3, background: HONEY }}
            />
            <motion.p
              variants={fadeUp}
              className="leading-relaxed max-w-2xl mx-auto mb-8 text-[17px] md:text-[19px]"
              style={{ color: 'rgba(10,31,56,0.75)' }}
            >
              Sturent didn&apos;t start in a boardroom. It started with a
              problem worth solving — and a story worth telling.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/our-story" data-testid="origin-cta">
                <Button
                  className="h-13 px-7 py-5 rounded-full font-semibold text-[#0A1F38] transition-transform hover:-translate-y-[1px]"
                  style={{
                    background: HONEY,
                  }}
                >
                  Read the founder&apos;s story
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ------------------ Verified GTA listings — coming soon ---------------- */}
      <section
        className="relative py-24 lg:py-28"
        style={{ background: CREAM }}
      >
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              className="text-center text-xs font-bold uppercase tracking-[0.18em] mb-3"
              style={{ color: HONEY_DARK }}
            >
              Listings
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="text-center font-extrabold leading-[1.05] tracking-tight text-[#0A1F38] mb-10"
              style={{
                fontFamily: "'Inter Tight', 'Inter', sans-serif",
                fontSize: 'clamp(1.75rem, 3.4vw, 2.75rem)',
              }}
            >
              Verified GTA listings, coming soon.
            </motion.h2>

            <motion.div variants={fadeUp}>
              <div
                className="rounded-[24px] p-8 sm:p-10 text-center"
                style={{
                  background: 'white',
                  border: `1px solid ${HONEY}33`,
                  boxShadow: '0 20px 50px -20px rgba(10,31,56,0.12)',
                }}
                data-testid="listings-coming-soon-card"
              >
                <div
                  className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: `${HONEY}22` }}
                >
                  <MapPin className="h-8 w-8" style={{ color: HONEY_DARK }} />
                </div>
                <p className="text-[#0A1F38]/80 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                  We&apos;re working with landlords in{' '}
                  <span className="font-bold text-[#0A1F38]">
                    Brampton, Mississauga,
                  </span>{' '}
                  and{' '}
                  <span className="font-bold text-[#0A1F38]">Scarborough</span>{' '}
                  to launch verified, all-in-priced listings near Sheridan,
                  Humber, UTM, Conestoga, and Centennial.
                </p>
                <Button
                  onClick={goSignup}
                  className="mt-7 h-13 px-7 py-5 rounded-full font-semibold text-[#0A1F38] transition-transform hover:-translate-y-[1px]"
                  style={{
                    background: HONEY,
                  }}
                  data-testid="waitlist-btn"
                >
                  Join the waitlist
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* --------------------------- Stats strip — navy ------------------------ */}
      <section
        className="relative overflow-hidden py-20 lg:py-24"
        style={{
          background: `linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)`,
          color: CREAM,
        }}
        data-testid="stats-strip"
      >
        <div
          aria-hidden
          className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl"
          style={{ background: `${HONEY}22` }}
        />
        <div
          aria-hidden
          className="absolute bottom-0 left-0 w-72 h-72 rounded-full blur-3xl"
          style={{ background: 'rgba(16,185,129,0.12)' }}
        />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 md:gap-x-8">
            {/* 1 — 100% verified */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: 0.0 }}
              className="text-center md:text-left"
              data-testid="stat-verified"
            >
              <div
                className="leading-none mb-3 text-[56px] md:text-[72px] lg:text-[88px]"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  color: HONEY,
                }}
              >
                <CountUp to={100} />%
              </div>
              <p
                className="leading-snug max-w-[220px] mx-auto md:mx-0 text-[16px]"
                style={{ color: 'rgba(250,247,242,0.65)' }}
              >
                of landlords verified before listings go live
              </p>
            </motion.div>

            {/* 2 — $0 hidden fees */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center md:text-left"
              data-testid="stat-fees"
            >
              <div
                className="leading-none mb-3 text-[56px] md:text-[72px] lg:text-[88px]"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  color: HONEY,
                }}
              >
                $<CountUp to={0} />
              </div>
              <p
                className="leading-snug max-w-[220px] mx-auto md:mx-0 text-[16px]"
                style={{ color: 'rgba(250,247,242,0.65)' }}
              >
                hidden fees on any place. Ever.
              </p>
            </motion.div>

            {/* 3 — 5 min verify */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center md:text-left"
              data-testid="stat-verify-time"
            >
              <div
                className="leading-none mb-3 text-[56px] md:text-[72px] lg:text-[88px]"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  color: HONEY,
                }}
              >
                <CountUp to={5} />min
              </div>
              <p
                className="leading-snug max-w-[220px] mx-auto md:mx-0 text-[16px]"
                style={{ color: 'rgba(250,247,242,0.65)' }}
              >
                to verify yourself and unlock messaging
              </p>
            </motion.div>

            {/* 4 — 24/7 support */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center md:text-left"
              data-testid="stat-support"
            >
              <div
                className="leading-none mb-3 text-[56px] md:text-[72px] lg:text-[88px]"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  color: HONEY,
                }}
              >
                <CountUp to={24} />/7
              </div>
              <p
                className="leading-snug max-w-[220px] mx-auto md:mx-0 text-[16px]"
                style={{ color: 'rgba(250,247,242,0.65)' }}
              >
                support for both sides of every deal
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --------------------------------- Footer ------------------------------ */}
      <footer
        className="border-t border-white/10"
        style={{ background: NAVY_DEEP, color: 'rgba(229,223,211,0.85)' }}
        data-testid="site-footer"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12">
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img
                  src={LOGO_URL}
                  alt="Sturent"
                  className="h-9 w-9 rounded-xl object-cover"
                />
                <span
                  className="font-extrabold text-lg tracking-tight"
                  style={{ color: CREAM, fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                >
                  Sturent
                </span>
              </div>
              <p
                className="font-semibold text-base mb-3"
                style={{ color: CREAM, fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
              >
                Built for students. Trusted by landlords.
              </p>
              <p className="text-sm leading-relaxed max-w-sm">
                Sturent is a student housing marketplace launching in the
                Greater Toronto Area. Verified landlords, real prices, no
                guesswork.
              </p>
            </div>

            <div>
              <h3
                className="text-sm font-bold mb-4"
                style={{ color: CREAM }}
              >
                Find a place
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link
                    to="/search"
                    className="hover:text-white transition-colors"
                    data-testid="footer-find-home"
                  >
                    Find a home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/signup"
                    className="hover:text-white transition-colors"
                    data-testid="footer-create-account"
                  >
                    Create a free account
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="hover:text-white transition-colors"
                    data-testid="footer-sign-in"
                  >
                    Sign in
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold mb-4" style={{ color: CREAM }}>
                List a place
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link
                    to="/signup?role=landlord"
                    className="hover:text-white transition-colors"
                    data-testid="footer-list-property"
                  >
                    List your property
                  </Link>
                </li>
                <li>
                  <Link
                    to="/signup?role=landlord"
                    className="hover:text-white transition-colors"
                    data-testid="footer-get-verified"
                  >
                    Get verified
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold mb-4" style={{ color: CREAM }}>
                Company
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link
                    to="/about"
                    className="hover:text-white transition-colors"
                    data-testid="footer-about"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <a
                    href="mailto:hello@sturent.ca"
                    className="hover:text-white transition-colors"
                    data-testid="footer-contact"
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <Link
                    to="/help"
                    className="hover:text-white transition-colors"
                    data-testid="footer-help"
                  >
                    Help
                  </Link>
                </li>
              </ul>
            </div>

            <div className="lg:col-start-5">
              <h3 className="text-sm font-bold mb-4" style={{ color: CREAM }}>
                Legal
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="mt-12 pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <p className="text-xs" style={{ color: 'rgba(229,223,211,0.55)' }}>
              © 2026 Sturent. All rights reserved.
            </p>
            <p className="text-xs" style={{ color: 'rgba(229,223,211,0.55)' }}>
              Built in Brampton, Canada with care for students everywhere.
            </p>
          </div>
        </div>
      </footer>

      {/* ------------------------- Expansion-request dialog -------------------- */}
      <Dialog open={showExpansion} onOpenChange={handleDialogChange}>
        <DialogContent
          className="w-[calc(100%-2rem)] sm:max-w-md border-0 shadow-xl p-5 sm:p-6"
          style={{
            background: CREAM,
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
          data-testid="expansion-dialog"
        >
          {expansionStatus === 'success' ? (
            <>
              <DialogHeader className="text-center">
                <div
                  className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                  style={{ background: `${HONEY}22` }}
                >
                  <CheckCircle2 className="h-7 w-7" style={{ color: HONEY_DARK }} />
                </div>
                <DialogTitle
                  className="text-xl font-extrabold text-[#0A1F38]"
                  style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                >
                  Got it.
                </DialogTitle>
                <DialogDescription className="text-[#0A1F38]/70 mt-2 leading-relaxed">
                  We&apos;ll email you the moment we launch in{' '}
                  <span className="font-semibold text-[#0A1F38]">
                    {cityLabel}
                  </span>
                  . In the meantime, if you have friends or family heading to
                  the GTA, send them our way.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <Button
                  onClick={() => handleDialogChange(false)}
                  className="w-full py-6 text-base rounded-full font-semibold text-[#0A1F38]"
                  style={{ background: HONEY }}
                  data-testid="expansion-close-btn"
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader className="text-center">
                <div
                  className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                  style={{ background: `${HONEY}22` }}
                >
                  <MapPin className="h-7 w-7" style={{ color: HONEY_DARK }} />
                </div>
                <DialogTitle
                  className="text-lg sm:text-xl font-extrabold text-[#0A1F38] leading-snug"
                  style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}
                  data-testid="expansion-headline"
                >
                  We&apos;re not in {cityLabel} yet — but we&apos;re coming.
                </DialogTitle>
                <DialogDescription className="text-[#0A1F38]/70 mt-2 leading-relaxed">
                  Sturent is launching in the Greater Toronto Area in Fall
                  2026. Drop your email and we&apos;ll let you know the moment
                  we expand to{' '}
                  <span className="font-semibold text-[#0A1F38]">
                    {cityLabel}
                  </span>
                  .
                </DialogDescription>
              </DialogHeader>

              <form
                onSubmit={submitExpansion}
                className="mt-4 space-y-3"
                data-testid="expansion-form"
              >
                <div>
                  <label
                    htmlFor="expansion-email"
                    className="block text-xs font-semibold text-[#0A1F38]/80 mb-1.5"
                  >
                    Email
                  </label>
                  <Input
                    id="expansion-email"
                    type="email"
                    placeholder="you@school.edu"
                    value={expansionEmail}
                    onChange={(e) => {
                      setExpansionEmail(e.target.value);
                      if (expansionError) setExpansionError('');
                    }}
                    className="h-12 bg-white"
                    data-testid="expansion-email-input"
                    autoFocus
                  />
                </div>

                <div>
                  <label
                    htmlFor="expansion-school"
                    className="block text-xs font-semibold text-[#0A1F38]/80 mb-1.5"
                  >
                    Which school will you be attending?{' '}
                    <span className="font-normal text-[#0A1F38]/50">
                      (optional)
                    </span>
                  </label>
                  <SchoolAutocomplete
                    countries={expansionCountries}
                    placeholder="Search universities and colleges…"
                    value={expansionSchool?.name || ''}
                    onSelect={(p) => setExpansionSchool(p)}
                    className="[&_input]:h-12 [&_input]:bg-white"
                  />
                </div>

                {expansionError && (
                  <p
                    className="text-sm text-red-600"
                    data-testid="expansion-error"
                  >
                    {expansionError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={expansionStatus === 'submitting'}
                  className="w-full py-6 rounded-full text-sm sm:text-base whitespace-normal h-auto leading-snug font-semibold text-[#0A1F38]"
                  style={{ background: HONEY }}
                  data-testid="expansion-submit-btn"
                >
                  {expansionStatus === 'submitting'
                    ? 'Saving…'
                    : `Notify me when Sturent launches in ${cityLabel}`}
                  {expansionStatus !== 'submitting' && (
                    <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
                  )}
                </Button>

                <p className="text-xs text-[#0A1F38]/60 text-center pt-1">
                  Already in the GTA?{' '}
                  <Link
                    to="/signup"
                    className="font-semibold hover:underline"
                    style={{ color: HONEY_DARK }}
                    data-testid="expansion-gta-signup-link"
                  >
                    Sign up here
                  </Link>
                  .
                </p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
